import { useCallback, useEffect, useRef, useState } from 'react';
import { AGENT_COPY, AGENT_RUN_STEPS } from './landingContent';
import { LANDING_EVENTS, trackLandingEvent } from './landingAnalytics';
import { useLandingReveal, usePrefersReducedMotion, useViewedOnce } from './useLandingReveal';

const TYPING_MS_PER_CHAR = 34;
const SEND_HOLD_MS = 520;
const HOLD_AFTER_RUN_MS = 2200;
const VISIBLE_LOG_ROWS = 4;
const TOTAL_STEPS = AGENT_RUN_STEPS.length;
const PROMPT = AGENT_COPY.prompt;
const RULER_TICKS = ['0:00', '0:05', '0:10', '0:15', '0:20', '0:25', '0:30'];
const CLIPS = [
  { id: 1, label: 'Shot 1', width: 1.15 },
  { id: 2, label: 'Shot 2', width: 1 },
  { id: 3, label: 'Shot 3', width: 0.85 },
];

/**
 * Elapsed counter for the generation steps that really do wait on a
 * render job, mirroring the wait timer in the product's agent panel.
 */
function WaitTimer() {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return <span className="lp-agentviz__timer">{`0:${String(seconds).padStart(2, '0')}`}</span>;
}

function LogRow({ step, state }) {
  return (
    <li className={`lp-agentviz__row lp-agentviz__row--${state} lp-agentviz__row--${step.kind}`}>
      <span className="lp-agentviz__row-icon" aria-hidden="true">
        {state === 'active' ? <span className="lp-agentviz__spinner" /> : null}
        {state === 'done' ? (
          <svg viewBox="0 0 12 12" className="lp-agentviz__check">
            <path d="M2.5 6.4 L4.8 8.7 L9.5 3.6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : null}
      </span>
      <span className="lp-agentviz__row-copy">
        <strong className="lp-agentviz__row-title">{step.title}</strong>
        <span className="lp-agentviz__row-meta">{step.meta}</span>
      </span>
      {step.wait && state === 'active' ? <WaitTimer /> : null}
    </li>
  );
}

/** Multi-track timeline: video, audio, and caption lanes under a time ruler. */
function Timeline({ clipState, audioState, captionState, playing, parked }) {
  return (
    <div className="lp-agentviz__tl">
      <div className="lp-agentviz__tl-grid">
        <span aria-hidden="true" />
        <div className="lp-agentviz__ruler" aria-hidden="true">
          {RULER_TICKS.map((tick) => (
            <span className="lp-agentviz__tick" key={tick}>
              {tick}
            </span>
          ))}
        </div>

        <span className="lp-agentviz__tl-name">V1</span>
        <div className="lp-agentviz__lane" aria-hidden="true">
          {CLIPS.map((clip) => (
            <span
              key={clip.id}
              className={`lp-agentviz__clip lp-agentviz__clip--${clip.id} lp-agentviz__clip--${clipState(clip.id)}`}
              style={{ flexGrow: clip.width }}
            >
              <span className="lp-agentviz__clip-head">{clip.label}</span>
              <span className="lp-agentviz__clip-frames" />
            </span>
          ))}
        </div>

        <span className="lp-agentviz__tl-name">A1</span>
        <div className="lp-agentviz__lane lp-agentviz__lane--audio" aria-hidden="true">
          <span className={`lp-agentviz__audio lp-agentviz__audio--${audioState}`}>
            {Array.from({ length: 96 }, (_, bar) => (
              <span key={bar} style={{ height: `${22 + Math.abs(Math.sin(bar * 1.1) * Math.cos(bar * 0.37)) * 70}%` }} />
            ))}
          </span>
        </div>

        <span className="lp-agentviz__tl-name">TXT</span>
        <div className="lp-agentviz__lane lp-agentviz__lane--text" aria-hidden="true">
          {[0, 1, 2, 3].map((block) => (
            <span
              key={block}
              className={`lp-agentviz__caption lp-agentviz__caption--${captionState}`}
              style={{ animationDelay: `${block * 70}ms` }}
            />
          ))}
        </div>

        {playing || parked ? (
          <span
            className={`lp-agentviz__playhead${parked ? ' lp-agentviz__playhead--parked' : ''}`}
            aria-hidden="true"
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * Scripted replay of a Composer run: the prompt types itself, sends, and
 * the agent works through the step list. A single `index` drives the run
 * and everything downstream (log window, clips, audio, captions,
 * playhead) derives from it, so there is one source of truth and no
 * animation state to keep in sync. The run only advances while the
 * visual is on screen, and reduced motion renders the finished state.
 */
function AgentRunVisual({ reducedMotion }) {
  const [phase, setPhase] = useState(reducedMotion ? 'running' : 'typing');
  const [typed, setTyped] = useState(reducedMotion ? PROMPT.length : 0);
  const [index, setIndex] = useState(reducedMotion ? TOTAL_STEPS : -1);
  const [playing, setPlaying] = useState(false);
  const containerRef = useRef(null);

  // Only run while the visual is actually on screen.
  useEffect(() => {
    if (reducedMotion) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setPlaying(entry.isIntersecting),
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  const idle = reducedMotion || !playing;

  // The prompt types itself, then the send button fires.
  useEffect(() => {
    if (idle || phase !== 'typing') return undefined;
    if (typed >= PROMPT.length) {
      const timer = window.setTimeout(() => setPhase('sending'), SEND_HOLD_MS);
      return () => window.clearTimeout(timer);
    }
    const timer = window.setTimeout(() => setTyped((value) => value + 1), TYPING_MS_PER_CHAR);
    return () => window.clearTimeout(timer);
  }, [idle, phase, typed]);

  useEffect(() => {
    if (idle || phase !== 'sending') return undefined;
    const timer = window.setTimeout(() => {
      setPhase('running');
      setIndex(0);
    }, SEND_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [idle, phase]);

  // `index` walks each step, then TOTAL_STEPS (finished, holding on the
  // delivered cut) before the whole run restarts from an empty prompt.
  useEffect(() => {
    if (idle || phase !== 'running') return undefined;
    const delay = index < TOTAL_STEPS ? AGENT_RUN_STEPS[index].duration : HOLD_AFTER_RUN_MS;
    const timer = window.setTimeout(() => {
      if (index < TOTAL_STEPS) {
        setIndex(index + 1);
        return;
      }
      setPhase('typing');
      setTyped(0);
      setIndex(-1);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [idle, phase, index]);

  const activeStep = AGENT_RUN_STEPS[index] ?? null;
  const emitted = AGENT_RUN_STEPS.slice(0, index + 1);
  const completed = AGENT_RUN_STEPS.slice(0, Math.max(0, Math.min(index, TOTAL_STEPS)));
  const rendered = phase === 'running' && index >= TOTAL_STEPS;
  const rendering = Boolean(activeStep?.final);

  // A lane item is pending while its step runs, then lands for good.
  const laneState = (match) => {
    if (completed.some(match)) return 'in';
    if (activeStep && match(activeStep)) return 'pending';
    return 'empty';
  };
  const clipState = (id) => laneState((step) => step.clip === id);

  // Newest rows sit at the bottom; older ones fall out of the window.
  const visibleSteps = emitted.slice(-VISIBLE_LOG_ROWS);
  const promptText = PROMPT.slice(0, typed);
  // The arrow arms as the prompt completes, then goes inert once sent.
  const sendReady = typed >= PROMPT.length && phase !== 'running';

  return (
    <div
      ref={containerRef}
      className={`lp-agentviz${rendered ? ' lp-agentviz--rendered' : ''}`}
      role="img"
      aria-label={AGENT_COPY.visualAlt}
    >
      <div className={`lp-agentviz__composer lp-agentviz__composer--${phase}`}>
        <span className="lp-agentviz__composer-text">
          {promptText}
          {phase === 'typing' ? <span className="lp-agentviz__caret" aria-hidden="true" /> : null}
        </span>
        <span
          className={`lp-agentviz__send${sendReady ? ' lp-agentviz__send--ready' : ''}${
            phase === 'sending' ? ' lp-agentviz__send--pressed' : ''
          }`}
          aria-hidden="true"
        >
          <svg viewBox="0 0 14 14">
            <path d="M2.5 7h9M7.6 3l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>

      <ol className="lp-agentviz__log">
        {visibleSteps.map((step) => (
          <LogRow key={step.id} step={step} state={step.id === activeStep?.id ? 'active' : 'done'} />
        ))}
      </ol>

      <div className="lp-agentviz__tl-head">
        <span className="lp-agentviz__tl-title">Timeline</span>
        {rendered ? <span className="lp-agentviz__tl-chip">Ready to edit</span> : null}
      </div>

      <Timeline
        clipState={clipState}
        audioState={laneState((step) => step.music)}
        captionState={laneState((step) => step.captions)}
        playing={rendering}
        parked={rendered}
      />
    </div>
  );
}

function ComposerAgentShowcase() {
  const reducedMotion = usePrefersReducedMotion();
  const [revealRef, revealed] = useLandingReveal();
  const visible = revealed || reducedMotion;

  const handleViewed = useCallback(() => {
    trackLandingEvent(LANDING_EVENTS.agentViewed);
  }, []);
  const viewedRef = useViewedOnce(0.5, handleViewed);

  return (
    <section
      id="product"
      ref={viewedRef}
      className="lp-section lp-agent"
      aria-labelledby="lp-agent-heading"
    >
      <div className="lp-container">
        <div
          ref={revealRef}
          className={`lp-reveal lp-agent__grid${visible ? ' lp-reveal--visible' : ''}`}
        >
          <div className="lp-agent__copy">
            <p className="lp-label">{AGENT_COPY.label}</p>
            <h2 id="lp-agent-heading" className="lp-heading">
              {AGENT_COPY.heading}
            </h2>
            <p className="lp-body">{AGENT_COPY.body}</p>
            <p className="lp-agent__supporting">{AGENT_COPY.supporting}</p>
          </div>
          <div className="lp-agent__art">
            <AgentRunVisual reducedMotion={reducedMotion} />
          </div>
        </div>
      </div>
    </section>
  );
}

export default ComposerAgentShowcase;
